// File: frontend/src/components/layout/Header.js

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../contexts/LanguageContext';
import AuthModal, { MODAL_TYPES } from '../auth/AuthModal';
import QuoteModal from '../modals/QuoteModal';
import styles from './Header.module.css';
import { ASSETS_BASE_URL } from '../../config/constants';

const Header = () => {
  const { isAuthenticated, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalType, setAuthModalType] = useState(MODAL_TYPES.SIGN_IN);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const isHome = location.pathname === '/';

  // Scroll detection
  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Handle hash navigation on mount and hash change
  useEffect(() => {
    if (location.hash) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const id = location.hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.hash, location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      if (window.__lenis) window.__lenis.stop();
    } else {
      document.body.style.overflow = '';
      if (window.__lenis) window.__lenis.start();
    }
    return () => {
      document.body.style.overflow = '';
      if (window.__lenis) window.__lenis.start();
    };
  }, [mobileMenuOpen]);

  // Close mobile menu when modals open
  useEffect(() => {
    if (showAuthModal || showQuoteModal) {
      setMobileMenuOpen(false);
    }
  }, [showAuthModal, showQuoteModal]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleSignIn = () => {
    setAuthModalType(MODAL_TYPES.SIGN_IN);
    setShowAuthModal(true);
    setMobileMenuOpen(false);
  };

  const handleSignUp = () => {
    setAuthModalType(MODAL_TYPES.SIGN_UP);
    setShowAuthModal(true);
    setMobileMenuOpen(false);
  };

  const handleGetQuote = () => {
    setShowQuoteModal(true);
    setMobileMenuOpen(false);
  };

  const handleDashboard = () => {
    navigate('/dashboard');
    setMobileMenuOpen(false);
  };

  const handleNavClick = (hash) => {
    setMobileMenuOpen(false);
    
    if (location.pathname !== '/') {
      // Navigate to home with hash
      navigate(`/${hash}`);
    } else {
      // Already on home, just update hash and scroll
      navigate(hash, { replace: true });
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  // Determine header visual state
  const isTransparent = isHome && !isScrolled && !mobileMenuOpen;

  const headerClasses = [
    styles.header,
    isScrolled && styles.scrolled,
    isTransparent && styles.transparent,
    mobileMenuOpen && styles.menuOpen,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <header className={headerClasses}>
        <div className={styles.container}>
          {/* Logo */}
          <Link to="/" className={styles.logo} onClick={() => setMobileMenuOpen(false)}>
            <img
              src={`${ASSETS_BASE_URL}/logos/allegory-logo-blue.svg`}
              alt={t('header.logo_alt', 'Allegory Insurance')}
              className={styles.logoImage}
            />
            {/* White logo variant for transparent state */}
            <img
              src={`${ASSETS_BASE_URL}/logos/allegory-logo-blue.svg`}
              alt={t('header.logo_alt', 'Allegory Insurance')}
              className={styles.logoImageLight}
            />
          </Link>

          {/* Desktop nav */}
          <nav className={`${styles.nav} ${mobileMenuOpen ? styles.navOpen : ''}`}>
            <div className={styles.navLinks}>
              <button 
                onClick={() => handleNavClick('#how-it-works')} 
                className={styles.navLink}
              >
                {t('header.nav.how_it_works', 'How It Works')}
              </button>
              <button 
                onClick={() => handleNavClick('#features')} 
                className={styles.navLink}
              >
                {t('header.nav.features', 'Features')}
              </button>
              <Link to="/products" className={styles.navLink} onClick={() => setMobileMenuOpen(false)}>
                {t('header.nav.products', 'Products')}
              </Link>
              {/* <button 
                onClick={() => handleNavClick('#pricing')} 
                className={styles.navLink}
              >
                {t('header.nav.pricing', 'Pricing')}
              </button> */}
              <Link to="/about" className={styles.navLink} onClick={() => setMobileMenuOpen(false)}>
                {t('header.nav.about', 'About')}
              </Link>
              <Link to="/contact" className={styles.navLink} onClick={() => setMobileMenuOpen(false)}>
                {t('header.nav.contact', 'Contact')}
              </Link>
            </div>

            {/* Mobile-only actions inside the slide menu */}
            <div className={styles.mobileActions}>
              <button onClick={handleGetQuote} className={styles.mobileQuoteButton}>
                {t('header.get_quote_button', 'Get a Quote')}
              </button>

              {isAuthenticated ? (
                <button onClick={handleDashboard} className={styles.mobileDashboardButton}>
                  {t('header.dashboard_button', 'Dashboard')}
                </button>
              ) : (
                <>
                  <button onClick={handleSignIn} className={styles.mobileSignInButton}>
                    {t('header.sign_in_button', 'Sign In')}
                  </button>
                  <button onClick={handleSignUp} className={styles.mobileSignUpButton}>
                    {t('header.get_started_button', 'Get Started Free')}
                  </button>
                </>
              )}
            </div>
          </nav>

          {/* Desktop actions */}
          <div className={styles.actions}>
            {isAuthenticated ? (
              <button onClick={handleDashboard} className={styles.dashboardButton}>
                {t('header.dashboard_button', 'Dashboard')}
              </button>
            ) : (
              <button onClick={handleSignIn} className={styles.signInLink}>
                {t('header.sign_in_button', 'Sign In')}
              </button>
            )}

            <button onClick={handleGetQuote} className={styles.quoteButton}>
              {t('header.get_quote_button', 'Get a Quote')}
            </button>

            {!isAuthenticated && (
              <button onClick={handleSignUp} className={styles.ctaButton}>
                {t('header.get_started_button', 'Get Started Free')}
              </button>
            )}
          </div>

          {/* Hamburger */}
          <button
            className={`${styles.menuToggle} ${mobileMenuOpen ? styles.menuToggleOpen : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={t('header.toggle_menu_aria', 'Toggle menu')}
            aria-expanded={mobileMenuOpen}
          >
            <span className={styles.menuBar} />
            <span className={styles.menuBar} />
            <span className={styles.menuBar} />
          </button>
        </div>
      </header>

      {/* Mobile backdrop */}
      {mobileMenuOpen && (
        <div
          className={styles.backdrop}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialType={authModalType}
      />

      <QuoteModal
        isOpen={showQuoteModal}
        onClose={() => setShowQuoteModal(false)}
        onOpenSignIn={handleSignIn}
        onOpenSignUp={handleSignUp}
      />
    </>
  );
};

export default Header;